/**
 * roadworthiness.ts — the contract's certificate, and the payment behind it.
 *
 * `get_preparation_status` EMITS NO MONEY FOR THIS ITEM. Its `roadworthiness` details are
 * `{ id, expires_at, certificate_url }` — the document and its clock, and nothing about the DEBIT
 * that bought the test. The read below adds that transaction, exactly the way the insurance premium
 * row reads its own (`data/insurance.ts`) rather than waiting on the RPC to grow a column.
 *
 * `select('*')` IS LOAD-BEARING, NOT LAZINESS. `vehicle_roadworthiness.transaction_id` arrives with
 * this feature's backend migration; naming the column in the select would fail this read with 42703
 * on every setup page in every environment the migration has not reached yet. A star select returns
 * whatever columns exist, so before it the payment is simply absent — which is the same thing an
 * unlinked certificate says after it, and the block prints nothing either way.
 *
 * THE ROW IS THE ONE THE BLOCK IS ALREADY DESCRIBING: the contract's oldest certificate, which is
 * what `get_preparation_status` picks (`ORDER BY rw.created_at ASC LIMIT 1`). Ordering this read
 * any other way would print one record's expiry over another record's payment.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';

/** Shares the `vehicle_roadworthiness` prefix, so a writer's prefix invalidation reaches it. */
export const contractRoadworthinessKey = (contractId: number) =>
    ['vehicle-roadworthiness', 'contract', contractId] as const;

/** The generated types predate the column — regenerate them once the migration has applied. */
type RoadworthinessRow = Tables<'vehicle_roadworthiness'> & { transaction_id?: number | null };

export interface RoadworthinessPayment {
    id: number;
    amount: number | null;
    transaction_at: string | null;
}

export interface ContractRoadworthiness {
    id: number;
    /**
     * The record's own two values, carried so the inline group can seed itself from THIS read
     * rather than from `get_preparation_status`. The RPC's details are a snapshot taken before the
     * last save; the group has to open on what the row holds now, or an admin re-opening a
     * certificate they just corrected sees the old date.
     */
    expiresAt: string | null;
    certificateUrl: string | null;
    /** Null both when the column has not landed yet and when the record simply has no payment. */
    transactionId: number | null;
    /** The linked DEBIT, or null — the link is optional and most records will not carry one. */
    payment: RoadworthinessPayment | null;
}

export async function fetchContractRoadworthiness(
    contractId: number,
): Promise<ContractRoadworthiness | null> {
    if (!contractId) return null;

    const { data, error } = await supabase
        .from('vehicle_roadworthiness')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
    if (error) throw error;

    const record = data as RoadworthinessRow | null;
    if (!record) return null;

    const base = {
        id: record.id,
        expiresAt: record.expires_at ?? null,
        certificateUrl: record.certificate_url ?? null,
    };

    const transactionId = record.transaction_id ?? null;
    if (!transactionId) return { ...base, transactionId: null, payment: null };

    const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .select('id, amount, transaction_at')
        .eq('id', transactionId)
        .maybeSingle();
    if (transactionError) throw transactionError;

    return { ...base, transactionId, payment: transaction ?? null };
}

/**
 * Create or amend the certificate, and upload the document if one was staged.
 *
 * THE PAYMENT KEY IS WRITTEN ONLY WHEN THE ADMIN SAID SOMETHING ABOUT IT — the rule the sheet
 * carried, moved here unchanged. `transaction_id` arrives with this feature's backend migration,
 * and PostgREST refuses the WHOLE save (PGRST204) for a column it has not seen, so a certificate
 * recorded with no payment — the common case, and the one that already works today — sends no key
 * at all and keeps working before the migration applies. Clearing an existing link still sends an
 * explicit `null`, because a link can only exist after it.
 *
 * THE CERTIFICATE IS REQUIRED AND THE PAYMENT IS NOT. `vehicle_roadworthiness.certificate_url` is
 * NOT NULL; the gate clears on the document, never on the money.
 */
export async function saveRoadworthiness({
    roadworthinessId,
    vehicleId,
    contractId,
    expiresAt,
    certificateUrl,
    certificateFile,
    transactionId,
    linkedTransactionId,
}: {
    /** The row to amend; null creates one. */
    roadworthinessId: number | null;
    vehicleId: number;
    contractId: number;
    expiresAt: string;
    /** What is already on file. */
    certificateUrl: string | null;
    certificateFile: File | null;
    /** What the picker holds now — null means "no payment". */
    transactionId: number | null;
    /** What the record holds today, which decides whether the key may be sent at all. */
    linkedTransactionId: number | null;
}): Promise<void> {
    if (!expiresAt) throw new Error('Enter the date the certificate expires.');
    if (!certificateUrl && !certificateFile) {
        throw new Error('Upload the certificate — the record cannot be saved without the document.');
    }

    let finalCertificateUrl = certificateUrl;
    if (certificateFile) {
        const fileExt = certificateFile.name.split('.').pop();
        const fileName = `certificates/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
            .from('vehicle-roadworthiness')
            .upload(fileName, certificateFile);
        if (uploadError) throw uploadError;
        finalCertificateUrl = supabase.storage
            .from('vehicle-roadworthiness')
            .getPublicUrl(fileName).data.publicUrl;
    }

    const writesTransaction = transactionId !== null || linkedTransactionId !== null;

    if (roadworthinessId != null) {
        const updateData: Record<string, unknown> = {
            expires_at: expiresAt,
            certificate_url: finalCertificateUrl,
        };
        if (writesTransaction) updateData.transaction_id = transactionId;

        const { error } = await supabase
            .from('vehicle_roadworthiness')
            .update(updateData)
            .eq('id', roadworthinessId);
        if (error) throw error;
        return;
    }

    const insertData: Record<string, unknown> = {
        vehicle_id: vehicleId,
        contract_id: contractId,
        expires_at: expiresAt,
        certificate_url: finalCertificateUrl,
    };
    if (writesTransaction) insertData.transaction_id = transactionId;

    const { error } = await supabase
        .from('vehicle_roadworthiness')
        .insert(insertData as TablesInsert<'vehicle_roadworthiness'>);
    if (error) throw error;
}
